import { FontAttributes } from '@loyalty/shared-common/template-api-client';
import { defaultFonts } from '@loyalty/quill';
import { useAlerts } from '@qualifio/ui-commons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../shared/api-client/ApiClientProvider';
import { useProgram } from '../../loyalty/ProgramContext';

// ============================================================================
// Query Keys
// ============================================================================

export const fontKeys = {
  all: ['fonts'] as const,
  byAccount: (accountId: string) => [...fontKeys.all, accountId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetches fonts for the current account.
 * Combines default fonts with custom fonts from the API.
 */
export function useFonts() {
  const { templateApi } = useApi();
  const { program } = useProgram();

  return useQuery({
    queryKey: fontKeys.byAccount(program.accountId),
    queryFn: async () => {
      const customFonts = await templateApi.getFonts(program.accountId);
      return [...defaultFonts, ...customFonts];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Mutation to add a new font.
 */
export function useAddFont() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { templateApi } = useApi();
  const { program } = useProgram();

  return useMutation({
    mutationFn: (font: FontAttributes) => templateApi.addFont(program.accountId, font),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fontKeys.byAccount(program.accountId) });
      notify(t('editor.custom-fonts.success.add-font'), 'success');
    },
    onError: error => {
      notify(t('editor.custom-fonts.errors.add-font', { error }), 'error');
    },
  });
}

/**
 * Mutation to update an existing font.
 */
export function useUpdateFont() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { templateApi } = useApi();
  const { program } = useProgram();

  return useMutation({
    mutationFn: (font: FontAttributes) => templateApi.updateFont(font),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fontKeys.byAccount(program.accountId) });
      notify(t('editor.custom-fonts.success.update-font'), 'success');
    },
    onError: error => {
      notify(t('editor.custom-fonts.errors.update-font', { error }), 'error');
    },
  });
}

/**
 * Mutation to delete a font.
 */
export function useDeleteFont() {
  const { notify } = useAlerts();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { templateApi } = useApi();
  const { program } = useProgram();

  return useMutation({
    mutationFn: (fontId: string) => templateApi.deleteFont(fontId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fontKeys.byAccount(program.accountId) });
      notify(t('editor.custom-fonts.success.delete-font'), 'success');
    },
    onError: error => {
      notify(t('editor.custom-fonts.errors.delete-font', { error }), 'error');
    },
  });
}
